import { isNil } from 'lodash';

export const leftPad = (number: number, targetLength = 2) => {
  if (isNil(number)) {
    return '0';
  }
  let output = `${number}`;
  while (output.length < targetLength) {
    output = `0${output}`;
  }
  return output;
};

/*
 *
 * - ROUND DOWN HALF -
 * * -> Always round down to nearest half
 * Example:
 * case number < 2.5: 2.4 => 2
 * case number === 2.5: 2.5 => 2.5
 * case number > 2.5: 2.6 => 2.5
 *
 * */
export const roundDownHalf = (overall = 0) => {
  return Math.floor(overall * 2) / 2;
};

/*
 *
 * - ROUND UP HALF -
 * -> Always round up to nearest half
 * Example:
 * case number < 2.5: 2.4 => 2.5
 * case number === 2.5: 2.5 => 2.5
 * case number > 2.5: 2.6 => 3
 *
 * */
export const roundUpHalf = (overall = 0) => {
  return Math.ceil(overall * 2) / 2;
};

/*
 *
 * - ROUND HALF -
 * -> Round up and down base on nearest - Base on 0.25 and 0.75
 * Example:
 * case number < 2.25: 2.24 => 2
 * case number >= 2.25 && number < 2.5: 2.25 => 2.5
 * case number === 2.5: 2.5 => 2.5
 * case number > 2.5 && number < 2.75: 2.74 => 2.5
 * case number >= 2.75: 2.75 => 3
 *
 * */
export const roundHalf = (overall = 0) => {
  return Math.round(overall * 2) / 2;
};

export const round = (value = 0) => {
  return Math.round(value);
};

/**
 * 0 => 0th
 * 1 => 1st
 * 2 => 2nd
 * etc.
 */
export const getOrdinal = (n: number) => {
  if (typeof n !== 'number') return n;

  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const isNumber = (n: number | string) => {
  return !isNil(n) && !isNaN(+n);
};

export const isEven = (n: number) => {
  return n % 2 === 0;
};

const SI_SYMBOL = ['', 'K', 'M', 'G', 'T', 'P', 'E'];

export const abbreviateNumber = (num: number): number | string => {
  // what tier? (determines SI symbol)
  // eslint-disable-next-line no-bitwise
  const tier = (Math.log10(Math.abs(num)) / 3) | 0;

  // if zero, we don't need a suffix
  if (tier === 0) return num;

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);

  // scale the number
  const scaled = num / scale;

  // format number and add suffix
  const result = scaled.toFixed(1);
  return (result.includes('.0') ? result.replace('.0', '') : result) + suffix;
};

export const toNumber = (value: string | any, defaultValue?: number) => {
  const converted = Number(value);

  if (isNaN(converted) && defaultValue !== undefined) {
    return defaultValue;
  }

  return converted;
};

export const getChangePercentage = (first, last) => {
  const f = first || 0;
  const l = last || 0;
  if (f === 0 || l === 0) {
    return 0;
  }
  const diff = l - f;
  const percent = (diff / f) * 100;

  return Math.round(percent);
};

export const findClosestNumber = (numberArray: number[], findNumber: number) => {
  const preCalculateArray = numberArray.map((eachNumber) => Math.abs(eachNumber - findNumber));
  const minNumber = Math.min(...preCalculateArray);
  const matchIndex = preCalculateArray.indexOf(minNumber);
  return {
    matchIndex,
    matchNumber: numberArray[matchIndex],
  };
};

export const fixedNumber = (value: number, fixed = 2) => {
  return parseFloat(value.toFixed(fixed)).toString();
};
